import { isPlatformServer, NgIf, NgClass, NgTemplateOutlet, NgFor } from '@angular/common';
import {
	ChangeDetectionStrategy,
	Component,
	HostListener,
	Inject,
	OnInit,
	Optional,
	PLATFORM_ID,
	ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormComponent } from '@woolworthsnz/form';
import {
	AnalyticsClass,
	ANALYTICS_PROVIDER,
	AppSettingsService,
	BreakPointService,
	CarouselComponent,
	CarouselDirective,
	DatalayerService,
	Device,
	ImpressionArgsFactory,
	ImpressionIntersectionService,
	ImpressionTrackingBehaviourType,
	PORTAL_DATA,
	PromoImpressionArgs,
	promoViewAnalytics,
	TrackImpressionDirective,
} from '@woolworthsnz/styleguide';
import { Container, LayoutMode } from '@woolworthsnz/trader-api';
import { CustomElementsFactoryService } from '../../services/custom-elements-factory.service';
import { PromoData } from '../../ui-models';
import { getAnalyticsData } from './dynamic-content.helpers';
import { Carousel2DCComponent } from '../carousel2-dc/carousel2-dc.component';
import { DynamicComponentDirective } from '../../directives/dynamic-component.directive';

export enum DynamicContentContainerLayout {
	flowWrap = 'flowwrap', // The default layout
	carousel = 'carousel',
	hidden = 'hidden',
}

@Component({
	selector: 'dc-container',
	template: `
		<form
			*ngIf="layout.form; else content"
			[formGroup]="formGroup"
			[formAction]="formActionEndpoint"
			cdxForm
			[transformer]="transformFormValues"
			(submissionSuccess)="onFormSubmitSuccess()"
			class="dcForm"
			[ngClass]="formSizeClass"
		>
			<ng-container *ngTemplateOutlet="content"></ng-container>
		</form>
		<ng-template #content>
			<ng-container *ngIf="!formSubmitted; else successMessage">
				<ng-container *ngFor="let row of layout.rows; let i = index; trackBy: trackRows">
					<div class="dynamic-content-row row-fluid" *ngIf="rowHasVisibleContainers(row.containers)">
						<ng-container *ngFor="let container of row.containers; trackBy: trackContainers">
							<div
								class="span{{ container.width }}"
								id="{{ container.id }}"
								*ngIf="!isHidden(container[dcMappedDevice].layout)"
							>
								<ng-container
									*ngIf="!isCarousel(container[dcMappedDevice].layout); else carousel2Template"
								>
									<ng-template
										*ngFor="let item of container.items; let pos = index"
										[dynamicComponent]="item.type"
										[carouselName]="container.name"
										[data]="item"
										[formGroup]="formGroup"
										[dynamicForm]="form"
										[position]="pos"
									></ng-template>
								</ng-container>
								<ng-template #carousel2Template>
									<dc-carousel2
										*ngIf="container[dcMappedDevice].carousel.isnewversion; else carouselTemplate"
										(itemsVisible)="trackImpressions($event, container.items, container.name)"
										[name]="container.name"
										[items]="container.items"
										[autoRotateInterval]="
											parseInt(container[dcMappedDevice].carousel.transitioninterval)
										"
										[displayType]="container[dcMappedDevice].carousel.displaytype"
										[align]="container[dcMappedDevice].carousel.align"
										[columns]="container[dcMappedDevice].carousel.numberindisplay"
									></dc-carousel2>
								</ng-template>
								<ng-template #carouselTemplate>
									<cdx-carousel
										[autoRotate]="true"
										[items]="container.items"
										[name]="container.name"
										[autoRotateInterval]="
											parseInt(container[dcMappedDevice].carousel.transitioninterval)
										"
										class="carousel--dynamic"
										(animateEmitter)="trackImpression($event, container.items, container.name)"
									>
										<div cdxCarousel data-carousel-wrap="true">
											<div
												*ngFor="let item of container.items; let pos = index"
												class="carousel-item"
												[cdxTrackImpression]="carouselPromoImpressionArgs.get(item.id)"
												[preventFallbackImpression]="true"
												[trackingBehaviour]="
													carouselImpressionTrackingBehaviour.get(container.id)
												"
											>
												<ng-template
													[dynamicComponent]="item.type"
													[data]="item"
													[position]="pos"
													[carouselName]="container.name"
													[isCarouselItem]="true"
												></ng-template>
											</div>
										</div>
									</cdx-carousel>
								</ng-template>
							</div>
						</ng-container>
					</div>
				</ng-container>
			</ng-container>
		</ng-template>
		<ng-template #successMessage>
			<h3>{{ layout.form.successMessage }}</h3>
		</ng-template>
	`,
	styleUrls: ['./dynamic-content.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		NgIf,
		FormsModule,
		FormComponent,
		ReactiveFormsModule,
		NgClass,
		NgTemplateOutlet,
		NgFor,
		DynamicComponentDirective,
		Carousel2DCComponent,
		CarouselComponent,
		CarouselDirective,
		TrackImpressionDirective,
	],
})
export class DynamicContentComponent implements OnInit {
	@ViewChild(FormComponent) form: FormComponent;
	device$ = this.breakPointService.device$.pipe(takeUntilDestroyed());

	device: Device;

	formGroup: FormGroup;
	formSubmitted = false;

	carouselPromoImpressionArgs: Map<string, PromoImpressionArgs> = new Map();
	carouselImpressionTrackingBehaviour: Map<string, ImpressionTrackingBehaviourType> = new Map();

	get formSizeClass(): string {
		// Backwards compatibility with existing forms. They could have formLayout as '' or 'Basic'
		if (!['narrow', 'medium', 'large'].includes(this.layout?.form?.formLayout)) {
			return 'dcForm--medium';
		}

		return `dcForm--${this.layout.form.formLayout}`;
	}

	get formActionEndpoint(): string {
		return `${this.appSettingsService.getEndpoint('dynamicContentFormSubmit')}?layoutId=${this.layout.id}`;
	}

	get dcMappedDevice(): string {
		return this.mapDevice();
	}

	constructor(
		@Inject(PORTAL_DATA) public layout: any,
		private breakPointService: BreakPointService,
		private appSettingsService: AppSettingsService,
		private datalayerService: DatalayerService,
		private impressionIntersectionService: ImpressionIntersectionService,
		private fb: FormBuilder,
		private customElementsFactoryService: CustomElementsFactoryService,
		private router: Router,
		@Inject(PLATFORM_ID) private platformId: Object,
		@Optional() @Inject(ANALYTICS_PROVIDER) private tealiumService: AnalyticsClass
	) {
		this.customElementsFactoryService.bootstrapCustomElements();
	}

	private static getPromoImpressionData(item: any, containerName: string, position: number): PromoData {
		return {
			id: `${item.id} - ${item.imagePath}`,
			name: item.name || item.text,
			carouselName: containerName,
			creative: 'Carousel',
			position: position,
			imagePath: item.imagePath,
			link: item.link,
		};
	}

	@HostListener('click', ['$event'])
	onContentClick = (e: MouseEvent): void => {
		let target = e.target;
		// Search up the event path to see if there is an anchor element wrapping whatever was clicked
		// Checking for composedPath since Edge 12-18 and IE don't support it
		if (e.composedPath) {
			const potentialPathAnchor = e.composedPath().find((el) => el instanceof HTMLAnchorElement);
			if (!(target instanceof HTMLAnchorElement) && potentialPathAnchor) {
				target = potentialPathAnchor;
			}
		}

		if (target instanceof HTMLAnchorElement && target.host === location.host) {
			// Bail out early if the link should be opened anywhere but the same frame
			if (target.target && target.target !== '_self') {
				return;
			}

			// Don't capture page anchor links
			if (target.attributes.getNamedItem('href')?.value.startsWith('#')) {
				return;
			}

			// Don't capture links to the same page (excluding hash)
			if (this.router.url.split('#')[0] === target.attributes.getNamedItem('href')?.value.split('#')[0]) {
				return;
			}

			e.preventDefault();

			// Need this so we don't drop query parameters
			const url = new URL(target.href);
			this.router.navigateByUrl(`${url.pathname}${url.search}`);
		}
	};

	ngOnInit(): void {
		this.initPromoImpressionArgs();

		this.formGroup = this.fb.group({}, { updateOn: 'blur' });
		this.device$.subscribe((device) => {
			this.device = device;
		});
	}

	rowHasVisibleContainers = (containers: Container[]): number =>
		containers.filter(
			(c: Container) => !this.isHidden((c[this.mapDevice() as keyof Container] as LayoutMode)?.layout)
		).length;

	/**
	 * server DC has only 3 viewport sizes configured so we need to map to our client side (more granular) viewport sizes
	 */
	mapDevice(): string {
		switch (this.device) {
			case Device.MOBILE:
				return 'mobile';
			case Device.TABLET:
			case Device.TABLETPORTRAIT:
				return 'tablet';
			default:
				return 'desktop';
		}
	}

	isCarousel = (layout: DynamicContentContainerLayout | string): boolean =>
		layout === DynamicContentContainerLayout.carousel;

	isHidden = (layout?: DynamicContentContainerLayout | string): boolean =>
		layout === DynamicContentContainerLayout.hidden;

	trackRows = (_: any, row: any): string => (row.containers[0] || {}).id;

	trackContainers = (_: any, container: any): string => (container.items[0] || {}).id;

	/**
	 * Track impressions manually if the browser doesn't support the IntersectionObserver API (canIntersect will be false)
	 * @param currentItem
	 * @param items
	 * @param containerName
	 */
	trackImpression = (currentItem: number, items: any[], containerName: string): void => {
		// If the intersection observer is supported we don't need to track this manually
		if (isPlatformServer(this.platformId) || this.impressionIntersectionService.canIntersect() || !items) {
			return;
		}

		const itemIndex = currentItem - 1;
		const item = items[itemIndex];
		if (!item) {
			return;
		}
		this.trackPromoAnalytics(DynamicContentComponent.getPromoImpressionData(item, containerName, currentItem));
	};

	/**
	 * Track impressions manually if the browser doesn't support the IntersectionObserver API (canIntersect will be false)
	 * @param visibleItems
	 * @param allItems
	 * @param containerName
	 */
	trackImpressions = (visibleItems: any[], allItems: any[], containerName: string): void => {
		// If the intersection observer is supported we don't need to track this manually
		if (isPlatformServer(this.platformId) || this.impressionIntersectionService.canIntersect() || !visibleItems) {
			return;
		}

		this.datalayerService.trackPromoImpression(
			visibleItems.map((item) =>
				DynamicContentComponent.getPromoImpressionData(item, containerName, allItems.indexOf(item) + 1)
			)
		);

		const promoData = getAnalyticsData(visibleItems);

		this.tealiumService.link({
			...promoViewAnalytics,
			media_type: 'Carousel',
			...promoData,
		});
	};

	parseInt = (numStr: string): number => parseInt(numStr, 10);

	trackPromoAnalytics(promoData: PromoData): void {
		this.datalayerService.trackPromoImpression(promoData);
		this.tealiumService.link({
			...promoViewAnalytics,
			media_label: [`${promoData.imagePath}`],
			media_category: [`${promoData.name}`],
			media_type: `${promoData.creative}`,
			media_carousel_slot: [`${promoData.position}`],
			media_destination_link: [`${promoData.link}`],
		});
	}

	onFormSubmitSuccess(): void {
		if (this.layout.form.hasSuccessMessageText) {
			this.formSubmitted = true;
		} else {
			this.router.navigateByUrl(this.layout.form.redirectUrl);
		}
	}

	transformFormValues = (v: { [key: string]: any }): Record<string, any> => this.flatten(v);

	/**
	 * Takes a form value, assuming checkboxes are nested under a control, and flattens them to a comma separated string.
	 * Ignores unchecked (false) valued keys
	 *
	 * If the input formObject was:
	 * ```
	 * {
	 *   someControl: 'a value',
	 *   someCheckboxes: {
	 *     'box1_val': true,
	 *     'box2_val2': false,
	 *     'box3_val3': true,
	 *   }
	 * }
	 * ```
	 * the output would be:
	 * ```
	 * {
	 *   someControl: 'a value',
	 *   someCheckboxes: 'val,val3',
	 * }
	 * ```
	 * @param formObject
	 * @private
	 */
	private flatten(formObject: { [key: string]: any }): Record<string, any> {
		Object.keys(formObject).forEach((key) => {
			const value = formObject[key];
			if (typeof value !== 'object' || value === null) {
				return;
			}

			const flattenedValue = Object.keys(value)
				.reduce((accum: string[], nestedKey) => {
					if (value[nestedKey]) {
						const idVal = nestedKey.split('_');
						if (idVal[1]) {
							accum.push(idVal[1]);
						} else {
							accum.push(nestedKey);
						}
					}
					return accum;
				}, [])
				.join(',');

			formObject[key] = flattenedValue;
		});
		return formObject;
	}

	private initPromoImpressionArgs(): void {
		const device = this.dcMappedDevice;

		if (this.layout && this.layout.rows) {
			this.layout.rows.forEach((row: any) => {
				row.containers.forEach((container: any) => {
					if (this.isCarousel(container[device]?.layout)) {
						const transitionInterval = this.parseInt(container[device].carousel.transitioninterval);
						const trackingBehaviour =
							transitionInterval > 0
								? ImpressionTrackingBehaviourType.TrackOnEachIntersection
								: ImpressionTrackingBehaviourType.TrackOnFirstIntersectionOnly;
						this.carouselImpressionTrackingBehaviour.set(container.id, trackingBehaviour);

						container.items.forEach((item: any, index: number) => {
							this.carouselPromoImpressionArgs.set(
								item.id,
								ImpressionArgsFactory.createCarouselPromoImpressionArgs(item, container.name, index + 1)
							);
						});
					}
				});
			});
		}
	}
}
